import { z } from 'zod';

const revenueCatProductSchema = z.object({
  object: z.literal('product'),
  id: z.string(),
  store_identifier: z.string(),
  type: z.enum(['subscription', 'one_time']),
  subscription: z
    .object({
      duration: z.string().nullish(),
      grace_period_duration: z.string().nullish(),
      trial_duration: z.string().nullish(),
    })
    .nullish(),
  one_time: z
    .object({
      is_consumable: z.boolean().nullish(),
    })
    .nullish(),
  created_at: z.number(),
  app_id: z.string(),
  display_name: z.string().nullish(),
});

export const revenueCatEntitlementSchema = z.object({
  object: z.literal('entitlement'),
  project_id: z.string(),
  id: z.string(),
  lookup_key: z.string(),
  display_name: z.string(),
  created_at: z.number(),
  products: z
    .object({
      object: z.literal('list'),
      next_page: z.string().nullish(),
      url: z.string(),
      items: z.array(revenueCatProductSchema),
    })
    .nullish(),
});

export type RevenueCatEntitlement = z.infer<typeof revenueCatEntitlementSchema>;

const revenueInUsdSchema = z.object({
  currency: z.string(),
  gross: z.number(),
  commission: z.number().optional(),
  tax: z.number(),
  proceeds: z.number(),
});

const sharedRevCatSchema = z.object({
  id: z.string(),
  customer_id: z.string(),
  original_customer_id: z.string(),
  product_id: z.string().nullish(),
  presented_offering_id: z.string().nullish(),
  entitlements: z.object({
    object: z.literal('list'),
    items: z.array(revenueCatEntitlementSchema),
    next_page: z.string().nullish(),
    url: z.string(),
  }),
  environment: z.enum(['sandbox', 'production']),
  store: z.enum(['amazon', 'app_store', 'mac_app_store', 'play_store', 'promotional', 'stripe', 'rc_billing', 'roku']),
  store_subscription_identifier: z.string(),
  ownership: z.string(),
  country: z.string().nullish(),
});

export const revenueCatSubscriptionSchema = sharedRevCatSchema.extend({
  object: z.literal('subscription'),
  starts_at: z.number(),
  current_period_starts_at: z.number(),
  current_period_ends_at: z.number().nullish(),
  gives_access: z.boolean(),
  pending_payment: z.boolean(),
  auto_renewal_status: z.enum([
    'will_renew',
    'will_not_renew',
    'will_change_product',
    'will_pause',
    'requires_price_increase_consent',
    'has_already_renewed',
  ]),
  status: z.enum([
    'trialing',
    'active',
    'expired',
    'in_grace_period',
    'in_billing_retry',
    'paused',
    'unknown',
    'incomplete',
  ]),
  total_revenue_in_usd: revenueInUsdSchema,
  pending_changes: z
    .object({
      product: revenueCatProductSchema.nullish(),
    })
    .nullish(),
  management_url: z.string().nullish(),
});

export type RevenueCatSubscription = z.infer<typeof revenueCatSubscriptionSchema>;

const revenueCatPurchaseSchema = sharedRevCatSchema.extend({
  object: z.literal('purchase'),
  purchased_at: z.number(),
  revenue_in_usd: revenueInUsdSchema,
  quantity: z.number(),
  status: z.enum(['owned', 'refunded']),
});

export type RevenueCatPurchase = z.infer<typeof revenueCatPurchaseSchema>;
